import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState } from 'react';
import { pitchLabel } from '@/utils/pitches';

export interface PitchSelectorOption {
  value: string;
  label: string;
}

/** Modo `byPitch` (tabela de um vídeo só): uma opção por pitch, valor = id. */
export function byPitchOptions(
  pitches: ReadonlyArray<{ id: string; name?: string | null }>,
): PitchSelectorOption[] {
  return pitches.map((pitch, index) => ({ value: pitch.id, label: pitchLabel(pitch, index) }));
}

/**
 * Modo `byPosition` (tabela que compara vídeos diferentes): "1º pitch" … "Nº pitch",
 * com N = maior número de pitches entre os vídeos. Valor = índice ("0", "1"…).
 */
export function byPositionOptions(
  pitchLists: ReadonlyArray<ReadonlyArray<unknown> | null | undefined>,
): PitchSelectorOption[] {
  const max = pitchLists.reduce((acc, list) => Math.max(acc, list?.length ?? 0), 0);
  return Array.from({ length: max }, (_, index) => ({ value: String(index), label: translateInterfaceText("{value0}º pitch", { value0: (index + 1) }) }));
}

/** A opção escolhida se ela ainda existe; senão a 1ª (null quando não há opção). */
export function resolvePitchSelection(options: PitchSelectorOption[], selected: string | null): string | null {
  if (selected !== null && options.some((option) => option.value === selected)) return selected;
  return options[0]?.value ?? null;
}

/** Estado do seletor: começa no 1º pitch e volta para ele quando o escolhido some. */
export function usePitchSelection(options: PitchSelectorOption[]): [string | null, (value: string) => void] {
  useLanguage();
  const [selected, setSelected] = useState<string | null>(null);
  return [resolvePitchSelection(options, selected), setSelected];
}
